import { localStorageKeyBasicInfo, localStorageKeyInterests } from "../../../components/LocalStorageProcesser";
import { AwardStatus, Campus, Experience, ExperienceType, IStudent, Pronouns, Semester, YesNoMaybeAlready } from "../../../api/client";
import { SelectOption } from "../../../components/UiComponents/selectFieldGroup";

// Options for selectFields (dropdowns) for the onboarding form
export const pronounsOptions: SelectOption[] = [
  { value: String(Pronouns.HeHim), label: "He/Him" },
  { value: String(Pronouns.SheHer), label: "She/Her" },
  { value: String(Pronouns.TheyThem), label: "They/Them" },
];

export const semesterOptions: SelectOption[] = [
  { value: String(Semester.Fall), label: "Fall" },
  { value: String(Semester.Spring), label: "Spring" },
  { value: String(Semester.Summer), label: "Summer" },
];

export const awardStatusOptions: SelectOption[] = [
  { value: String(AwardStatus.Accepted), label: "Accepted" },
  { value: String(AwardStatus.Declined), label: "Declined" },
  { value: String(AwardStatus.HonorableMention), label: "Honorable Mention" },
];

export const campusOptions: SelectOption[] = [
  { value: String(Campus.Tampa), label: "Tampa" },
  { value: String(Campus.StPetersburg), label: "St. Petersburg" },
  { value: String(Campus.SarasotaManatee), label: "Sarasota-Manatee" },
];


export const yesNoMaybeAlreadyOptions: SelectOption[] = [
  { value: String(YesNoMaybeAlready.Yes), label: "Yes" },
  { value: String(YesNoMaybeAlready.Maybe), label: "Maybe" },
  { value: String(YesNoMaybeAlready.No), label: "No" },
  { value: String(YesNoMaybeAlready.Already), label: "I am currently in graduate school" }
];

export const yesNoMaybeDonNotKnowOptions: SelectOption[] = [
  { value: String(YesNoMaybeAlready.Yes), label: "Yes" },
  { value: String(YesNoMaybeAlready.Maybe), label: "Maybe" },
  { value: String(YesNoMaybeAlready.No), label: "No" },
  { value: String(YesNoMaybeAlready.DoNotKnow), label: "I do not know about this" }
];

export const yesNoOptions: SelectOption[] = [
  { value: String(YesNoMaybeAlready.Yes), label: "Yes" },
  { value: String(YesNoMaybeAlready.No), label: "No" },
];

export const yesNoMaybeOptions: SelectOption[] = [
  { value: String(YesNoMaybeAlready.Yes), label: "Yes" },
  { value: String(YesNoMaybeAlready.No), label: "No" },
  { value: String(YesNoMaybeAlready.Maybe), label: "Maybe" },
];

export const experienceTypeOptions: SelectOption[] = [
  { value: String(ExperienceType.CommunityService), label: "Community Service" },
  { value: String(ExperienceType.StudentOrganizations), label: "Student Organizations" },
  { value: String(ExperienceType.Job), label: "Job" },
  { value: String(ExperienceType.Internship), label: "Internship" },
  { value: String(ExperienceType.Research), label: "Research" },
  { value: String(ExperienceType.CreativeProjects), label: "Creative Projects" },
  { value: String(ExperienceType.Writing), label: "Writing" },
  { value: String(ExperienceType.Sports), label: "Sports" },
  { value: String(ExperienceType.MusicalEnsembles), label: "Musical Ensembles" },
  { value: String(ExperienceType.PoliticalCampaigns), label: "Political Campaigns" },
  { value: String(ExperienceType.Publications), label: "Publications" },
  { value: String(ExperienceType.ConferencePresentations), label: "Conference Presentations" },
  { value: String(ExperienceType.StudyAbroad), label: "Study Abroad" },
  { value: String(ExperienceType.Other), label: "Other" },
];

// Interfaces for the second part of the form
export interface InterestsAndActivitiesInitialData {
  academicInterestsList: string[]; //keep
  personalInterestsList: string[]; // keep
  activityList: string[]; // keep
  gradSchoolPlans: string; // keep
  gradProgramsAbroadInterest: string; // keep
  futureGoals: string; // keep
  languagesSpokenList: string[];
  passionProjects: string;
  knowsHipsValue: string;
  experienceList: Experience[];
};

//Interface for the part of the form
export interface BasicInformationInitialData {
  preferredName: string;
  preferredPronouns: string;
  graduationSemester: string; // confirmed that want to keep
  graduationYear: string | number; // confirmed that want to keep
  linkedinProfile: string; // confirmed that want to keep
  resume: File | null; // confirmed that want to keep
  studentClassification: string[]; // confirmed that want to keep
  scholarshipPrograms: string[]; // confirmed that want to keep
  isUndergraduate: string; // new form
}

// Combined interface for the final form combination to submit it
export interface EntireFormInterface extends BasicInformationInitialData,
  InterestsAndActivitiesInitialData { };

// Prefils the form with data on editing
export const takeStudentDataFromContextBasicInfo = (studentData: IStudent): BasicInformationInitialData => {
  const studentClassification: string[] = [];
  if (studentData.isHonorsStudent) {
    studentClassification.push('Honors College Student');
  }

  if (studentData.isHonorsDepartmentProgram) {
    studentClassification.push('Departmental Honors Program(s)')
  }

  if (studentData.isInternationalStudent) {
    studentClassification.push('International Student');
  }

  if (studentData.isTransferStudent) {
    studentClassification.push('Transfer Student');
  }

  if (studentData.isPellGrantRecipient) {
    studentClassification.push('Pell Grant Recipient');
  }


  const scholarships: string[] = [];
  if (studentData.wilcoxScholars) {
    scholarships.push('Wilcox Scholars');
  }

  if (studentData.kosoveSociety) {
    scholarships.push('Kosove Society');
  }

  if (studentData.futureStrategies) {
    scholarships.push('GNSI Future Strategists');
  }

  if (studentData.peaceCorpPrep) {
    scholarships.push('Peace Corp Prep');
  }

  if (studentData.isNationalMeritScholar) {
    scholarships.push('National Merit Scholar');
  }

  return {
    preferredName: studentData.preferredName ?? "",
    preferredPronouns: String(studentData.preferredPronouns),
    graduationSemester: String(studentData.expectedGraduationSemester),
    graduationYear: studentData.expectedGraduationYear ?? new Date().getFullYear(),
    linkedinProfile: studentData.linkedinProfile ?? "",
    resume: null,
    studentClassification: studentClassification,
    scholarshipPrograms: scholarships,
    isUndergraduate: studentData.isUndergraduateStudent ? "1" : "2",
  }
}

// Prefills the form with data when it is editing
export const takeStudentDataFromContextInterests = (studentData: IStudent): InterestsAndActivitiesInitialData => {
  return {
    academicInterestsList: studentData.academicInterests ? studentData.academicInterests.map(interest => interest.name) : [],
    personalInterestsList: studentData.personalInterests ? studentData.personalInterests.map(interest => interest.name) : [],
    activityList: studentData.currentActivities ? studentData.currentActivities.map(interest => interest.name) : [],
    gradSchoolPlans: String(studentData.gradSchoolPlans),
    gradProgramsAbroadInterest: String(studentData.gradSchoolAbroadPlans),
    futureGoals: studentData.futureGoals ?? "",
    languagesSpokenList: studentData.speakForeignLanguageList ? studentData.speakForeignLanguageList.map(language => language.name) : [],
    passionProjects: studentData.passionProjects ?? "",
    knowsHipsValue: String(studentData.knowsAboutHIPS),
    experienceList: studentData.experienceList ?? []
  }
}

// Initial data of the form for first step
const initialDataBasicInfo: BasicInformationInitialData = {
  preferredName: '',
  preferredPronouns: '',
  graduationSemester: '',
  graduationYear: '',
  linkedinProfile: '',
  studentClassification: [],
  scholarshipPrograms: [],
  resume: null,
  isUndergraduate: '2'
};

// Initial data of the form for second step
const initialDataInterests: InterestsAndActivitiesInitialData = {
  academicInterestsList: [],
  personalInterestsList: [],
  activityList: [],
  gradSchoolPlans: '',
  gradProgramsAbroadInterest: '',
  futureGoals: '',
  languagesSpokenList: [],
  passionProjects: '',
  knowsHipsValue: '',
  experienceList: []
};

// Getting date from localStorage for the first step of the form
export const loadFromLocalStorageBasicInfo = () => {
  let localStorageData = localStorage.getItem(localStorageKeyBasicInfo);
  const savedData: BasicInformationInitialData = localStorageData ? JSON.parse(localStorageData) : initialDataBasicInfo;
  if (savedData) {
    savedData.resume = null;
  }
  return savedData;
};

// Loads form values from local storage for the second step of the form
export const loadFromLocalStorageInterests = () => {
  let localStorageData = localStorage.getItem(localStorageKeyInterests);
  const savedData: InterestsAndActivitiesInitialData = localStorageData ? JSON.parse(localStorageData) : initialDataInterests;
  return savedData;
};

// Saving data in local storage
export const saveToLocalStorage = (
  key: string, 
  values: BasicInformationInitialData | InterestsAndActivitiesInitialData) => {
  localStorage.setItem(key, JSON.stringify(values));
};
